import { LogoThumb } from "../components/illustrations";
import type { CaseStudy } from "./types";

export const fairyHomes: CaseStudy = {
  id: "FairyHomes",
  card: {
    id: "FairyHomes",
    href: "/FairyHomes",
    image: "/assets/case-study/fh/01.webp",
    imageAlt: "FairyHomes.ae",
    title: "FairyHomes.ae",
    domain: "fairyhomes.ae",
    miniDescription:
      "Real-estate platform - AI search, listings, secure admin dashboard.",
    caseNum: "01 - Real estate - UAE",
    lead: "A full real-estate platform: AI-powered property search, live listings synced from PropertyFinder, and a secure role-based admin dashboard for agents.",
    tags: [
      { label: "TanStack Start" },
      { label: "pgvector AI search", key: true },
      { label: "Neon Postgres" },
      { label: "Cloudflare" },
      { label: "Admin" },
    ],
    outcomes: [
      "Natural-language AI search across the full listing catalogue",
      "Role-based secure admin - agents, listings, enquiries",
      "Designed and shipped from MVP to production",
    ],
    thumb: (
      <LogoThumb
        src="/assets/case-study/fh/fairyhomes.webp"
        alt="Fairy Homes"
        color="#5b8def"
      />
    ),
  },
  imageSizes: {
    "/assets/case-study/fh/01.webp": { width: 2160, height: 1350 },
    "/assets/case-study/fh/02.webp": { width: 2160, height: 1350 },
    "/assets/case-study/fh/03.webp": { width: 2137, height: 1350 },
    "/assets/case-study/fh/04.webp": { width: 2160, height: 1350 },
    "/assets/case-study/fh/05.webp": { width: 2160, height: 1350 },
  },
  content: {
    metaTitle: "FairyHomes.ae — Case Study · ThroughPilot",
    metaDescription:
      "How ThroughPilot shipped FairyHomes.ae: AI property search, PropertyFinder inventory sync, and a secure admin portal on Cloudflare.",
    path: "/FairyHomes",
    ogImage: "/assets/case-study/fh/01.webp",
    kicker: "Case study · Real estate · Dubai",
    title: (
      <>
        FairyHomes<em>.ae</em>
      </>
    ),
    liveHref: "https://fairyhomes.ae",
    lede: "A production real-estate platform for a Dubai agency — where buyers describe what they want in plain language and an AI finds it across thousands of live listings.",
    meta: [
      { label: "Role", value: "Design & Full-stack — end to end" },
      {
        label: "Status",
        value: (
          <a
            href="https://fairyhomes.ae"
            target="_blank"
            rel="noopener noreferrer"
          >
            Live · fairyhomes.ae ↗
          </a>
        ),
      },
      { label: "Service", value: "Launchpad — MVP → Production" },
    ],
    chips: [
      { label: "TanStack Start", key: true },
      { label: "React 19" },
      { label: "Neon Postgres + pgvector", key: true },
      { label: "Drizzle ORM" },
      { label: "OpenAI", key: true },
      { label: "Cloudflare Workers" },
      { label: "PropertyFinder API" },
      { label: "PostHog" },
    ],
    heroImage: {
      src: "/assets/case-study/fh/01.webp",
      alt: "FairyHomes.ae home page — Find Your Ideal Space in Dubai",
    },
    overview: {
      label: "The brief",
      title: (
        <>
          A property search that <em>speaks human.</em>
        </>
      ),
      body: (
        <>
          <p>
            Fairy Homes is an established Dubai agency — operating since{" "}
            <b>2015</b>, with more than <b>AED 8 billion</b> in transactions and{" "}
            <b>1,200+ properties sold</b>. Their listings already lived on
            PropertyFinder, but the portal experience was the same rigid grid of
            dropdowns every competitor used.
          </p>
          <p>
            They wanted a site that felt like talking to a good agent: tell it{" "}
            <i>"luxury villa with a sea view under 5M"</i> and get the right
            homes back — not a form with twelve filters. And they needed it to
            run itself, staying in sync with their PropertyFinder inventory with
            zero manual upkeep.
          </p>
          <p>
            We designed and built the whole thing — front of house, the AI
            search, the data pipeline, and a staff admin portal — and shipped it
            to production on Cloudflare's edge.
          </p>
        </>
      ),
    },
    highlights: {
      title: "What makes it work.",
      features: [
        {
          num: "01 — AI natural-language search",
          title: "Describe it, don't filter it.",
          media: {
            src: "/assets/case-study/fh/02.webp",
            alt: "FairyHomes listings — natural-language search for 'luxury villa with sea view under 5M'",
          },
          body: (
            <>
              <p>
                A query goes through two layers: an LLM parses intent into
                structured filters (type, beds, budget, emirate, furnishing),
                while the free-text "vibe" is matched against listing embeddings
                with a pgvector cosine search. Results rank on meaning, not
                keywords.
              </p>
              <div className="demo-query">
                <span className="ic">⌕</span> luxury villa with sea view under
                5M
                <span className="cur" />
              </div>
            </>
          ),
          points: [
            "GPT-4.1-mini parses queries into a strict JSON filter schema",
            "1,536-dim embeddings, HNSW cosine index for instant ranking",
            "Degrades gracefully — ranks by embedding alone if parsing fails",
          ],
        },
        {
          num: "02 — Always-fresh inventory",
          title: "Synced from PropertyFinder, hands-off.",
          flip: true,
          media: {
            src: "/assets/case-study/fh/03.webp",
            alt: "FairyHomes property detail — full specs synced from PropertyFinder",
          },
          body: (
            <p>
              A scheduled pipeline mirrors the agency's PropertyFinder Atlas
              data — listings, agents, photos, and SuperAgent performance stats
              — into Postgres. Every sync is idempotent, so it's safe to re-run
              any time, and staff never re-enter a thing.
            </p>
          ),
          points: [
            "Listings, agents, transactions & ratings kept in sync",
            "Soft-deletes listings PropertyFinder no longer returns",
            "RERA / BRN compliance fields carried through",
          ],
        },
        {
          num: "03 — A real admin portal",
          title: "The team runs it themselves.",
          media: {
            src: "/assets/case-study/fh/04.webp",
            alt: "FairyHomes admin portal — one-click PropertyFinder sync controls",
          },
          body: (
            <p>
              The agency's live numbers — properties sold, AED transaction
              volume, years active — stay current straight from the sync,
              surfaced on the site and managed from a secure staff portal: a
              stats dashboard, an enquiry pipeline, review moderation, and
              one-click sync controls.
            </p>
          ),
          points: [
            "Dashboard of listings, agents & AED transaction volume",
            "Contact-enquiry lifecycle: new → contacted → closed",
            "Review moderation + shareable review-request links",
          ],
        },
        {
          num: "04 — Real-time by webhook",
          title: "Webhook-driven, signed, instant.",
          flip: true,
          media: {
            src: "/assets/case-study/fh/05.webp",
            alt: "FairyHomes admin portal — PropertyFinder webhook subscriptions",
          },
          body: (
            <p>
              Beyond the scheduled sync, PropertyFinder pushes events the moment
              they happen — a listing goes live, gets taken down, or hits a
              RERA/DLD compliance action — to a signed callback that updates the
              matching row in seconds. No waiting for the next sync cycle.
            </p>
          ),
          points: [
            "Subscribes to listing.published, unpublished & compliance events",
            "HMAC-signed callbacks verified against a shared secret",
            "Per-event live re-sync keeps the database current",
          ],
        },
      ],
    },
    hood: {
      title: "Production-grade from day one.",
      cards: [
        {
          t: "Framework",
          v: "TanStack Start",
          d: "React 19 with file-based routing and server functions.",
        },
        {
          t: "Data",
          v: "Neon Postgres + pgvector",
          d: "Relational data with a 1,536-dim HNSW vector index for semantic search.",
        },
        {
          t: "ORM",
          v: "Drizzle",
          d: <>Type-safe schema &amp; migrations, end to end.</>,
        },
        {
          t: "AI",
          v: "OpenAI",
          d: "Query parsing + text-embedding-3-small for listing vectors.",
        },
        {
          t: "Runtime",
          v: "Cloudflare Workers",
          d: "Edge-rendered, deployed via Wrangler.",
        },
        {
          t: "Integration",
          v: "PropertyFinder Atlas",
          d: "Scheduled sync + webhooks for live inventory.",
        },
      ],
      note: (
        <>
          The hard part wasn't the UI — it was making search{" "}
          <b>feel intelligent and stay honest.</b> Structured filters guarantee
          correctness (a 3-bed budget cap is never "interpreted"), while
          embeddings handle the fuzzy human bits. Indexes are tuned for the
          exact hot paths, so it stays quick as the catalogue grows.
        </>
      ),
    },
    outcome: {
      label: "Outcome",
      title: "Shipped, live, and self-running.",
      metrics: [
        {
          n: (
            <>
              AED&nbsp;8<em>B</em>
            </>
          ),
          l: "Total transactions",
        },
        {
          n: (
            <>
              250+<em>+</em>
            </>
          ),
          l: "Active listings",
        },
        {
          n: (
            <>
              10<em>+</em>
            </>
          ),
          l: "Years in the Dubai market",
        },
        {
          n: (
            <>
              4.6<em>★</em>
            </>
          ),
          l: "Across 250+ reviews",
        },
      ],
    },
    prev: {
      href: "/#work",
      dir: "← Back",
      ttl: (
        <>
          All <em>work</em>
        </>
      ),
    },
    next: {
      href: "/1Mind",
      dir: "Next case →",
      ttl: (
        <>
          1<em>Mind.com</em>
        </>
      ),
    },
  },
};
